import {
  AnyPgColumn,
  bigint,
  boolean,
  date,
  foreignKey,
  index,
  integer,
  pgEnum,
  pgSequence,
  pgTable,
  serial,
  smallint,
  text,
  timestamp,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { orders } from '@trashlab/api/src/domains/orders/schemas/orders';
import { assets } from '@trashlab/api/src/domains/asset/schemas/assets';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { organization_site } from '@trashlab/api/src/domains/dispatch/schemas/organization-site';
import { dump_ticket_extraction_run } from '@trashlab/api/src/domains/ai/schemas/dump-ticket-extraction-run';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas/sku-v2';
import { sub_hauler } from '@trashlab/api/src/domains/orders/schemas/sub-hauler';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { asset_movement } from '@trashlab/api/src/domains/asset/schemas/asset-movement';
import { rental_chain } from '@trashlab/api/src/domains/orders/schemas/rental-chain';
import { service_line_v2 } from '@trashlab/api/src/domains/orders/schemas/service-line-v2';
import { business_line_config } from '@trashlab/api/src/domains/business-lines/schemas/business-line';
import { comment } from '@trashlab/api/src/domains/super-comments/schemas/comment';

export const service_line_status = pgEnum('service_line_status', [
  'SCHEDULED',
  'DELIVERED',
  'REMOVED',
  'UNKNOWN',
]);

/**
 * Represents a line item in a service order that tracks the delivery, pickup, and dumping of materials.
 * A service line connects customers, sites, materials, and assets involved in waste management operations.
 * It maintains the timeline of service events and associated costs.
 */
export const service_line = pgTable(
  'service_line',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** ID of the customer site where the service is performed */
    customerSiteId: uuid('customerSiteId').notNull(),
    /** Reference to the order that delivered the container/material */
    deliveryOrderId: uuid('deliveryOrderId').references(
      (): AnyPgColumn => orders.id,
    ),
    /** Type of material being handled */
    materialId: uuid('materialId'),
    /** Reference to the order that picked up the container/material */
    pickupOrderId: uuid('pickupOrderId').references(
      (): AnyPgColumn => orders.id,
    ),
    /** Auto-incrementing identifier for the service line */
    serialNumber: serial('serialNumber').unique(),
    /** Unique identifier for rental services */
    rentalNumber: varchar('rentalNumber', { length: 255 }).unique(),
    /** Type of product/service being provided */
    productId: uuid('productId'),
    // DO NOT USE
    activeAtTs: timestamp('activeAtTs', { withTimezone: true, mode: 'string' }),
    /** Site where materials were dumped */
    dumpOrganizationSiteId: uuid('dumpOrganizationSiteId'),
    // DO NOT USE
    endedAtTs: timestamp('endedAtTs', { withTimezone: true, mode: 'string' }),
    /** Container or equipment used for the service */
    assetId: uuid('assetId'),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** Reference to the pricing SKU */
    skuId: uuid('skuId'),
    /** Date when materials were dumped */
    dumpDate: date('dumpDate', { mode: 'date' }),
    /** Reference to third-party hauler if used */
    subHaulerId: uuid('subHaulerId'),
    /** Total cost for dumping materials (in cents) */
    totalDumpCost: bigint('totalDumpCost', { mode: 'number' })
      .default(0)
      .notNull(),
    /** Indicates if dump cost was manually set vs calculated */
    dumpCostSetManually: boolean('dumpCostSetManually')
      .default(false)
      .notNull(),
    /** Reference to AI extraction run that determined dump site */
    dumpOrganizationSiteIdSetByExtractionRunId: uuid(
      'dumpOrganizationSiteIdSetByExtractionRunId',
    ),
    /** Date of last automated billing charge */
    lastAutomatedChargeDate: date('lastAutomatedChargeDate'),
    /** Date of last automated multiple billing charge */
    lastAutomatedMultipleChargeDate: date('lastAutomatedMultipleChargeDate'),
    /** Date of last automated days-based charge */
    lastAutomatedChargeOfDaysDate: date('lastAutomatedChargeOfDaysDate'),
    /** Override date for pickup billing */
    pickupDateBillingOverride: date('pickupDateBillingOverride'),
    /** Date of last service performed */
    lastServiceDate: date('lastServiceDate'),
    /** Additional notes about the service */
    notes: text('notes').default('').notNull(),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /** Reference to the customer */
    customerId: uuid('customerId'),
    /** Date when materials/container was last delivered */
    lastDeliveredDate: date('lastDeliveredDate'),

    lastAssetMovementId: uuid('lastAssetMovementId').references(
      (): AnyPgColumn => asset_movement.id,
    ),
    reasonForChange: text('reasonForChange'),
    /** Quantity of items in this service line */
    quantity: integer('quantity').default(1).notNull(),
    /** Link to the rental chain anchor row */
    rentalChainId: uuid('rentalChainId').references(
      (): AnyPgColumn => rental_chain.id,
    ),
    /** Link to the service line v2 anchor row */
    serviceLineV2Id: uuid('serviceLineV2Id').references(
      (): AnyPgColumn => service_line_v2.id,
    ),
    /** Business line this service line belongs to */
    businessLineId: uuid('businessLineId').references(
      () => business_line_config.id,
    ),
    /** Service date of the delivery order (null if canceled) */
    sl_deliveryDate: date('sl_deliveryDate', { mode: 'date' }),
    /** Service date of the pickup order (null if canceled) */
    sl_pickupDate: date('sl_pickupDate', { mode: 'date' }),
    /** Current status of the service line */
    sl_status: service_line_status('sl_status'),
    /** Snapshot of days the container has been on site */
    sl_daysOnSite: integer('sl_daysOnSite'),
    /** Billing pickup date with override support */
    sl_billablePickupDate: date('sl_billablePickupDate', { mode: 'date' }),
    /** Whether the delivery order is completed */
    sl_deliveryIsCompleted: boolean('sl_deliveryIsCompleted'),
    /** Whether the pickup order is completed */
    sl_pickupIsCompleted: boolean('sl_pickupIsCompleted'),
    /** Valid delivery order id (non-canceled) */
    sl_validDeliveryOrderId: uuid('sl_validDeliveryOrderId'),
    /** Geofence radius in meters for autosense/autocomplete - null means use route default or driver setting */
    geofenceRadiusMeters: smallint('geofenceRadiusMeters'),
    /** Reference to the rental note comment (1:1 relationship) */
    rentalNoteCommentId: uuid('rentalNoteCommentId').references(
      (): AnyPgColumn => comment.id,
    ),
  },
  (table) => {
    return {
      site_merge_page_idx: index('service_line_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      createdAtTs_idx: index('service_line_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      customer_id_idx: index('service_line_customer_id_idx').on(
        table.customerId,
      ),
      customer_merge_page_idx: index('service_line_customer_merge_page_idx').on(
        table.tenantId,
        table.customerId,
        table.id,
      ),
      customer_site_id_idx: index('service_line_customer_site_id_idx').on(
        table.customerSiteId,
      ),
      delivery_order_id_idx: index('service_line_delivery_order_id_idx').on(
        table.deliveryOrderId,
      ),
      delivery_order_id_tenantId_idx: index(
        'service_line_delivery_order_id_tenantId_idx',
      ).on(table.deliveryOrderId, table.tenantId),
      pickup_order_id_idx: index('service_line_pickup_order_id_idx').on(
        table.pickupOrderId,
      ),
      pickup_order_id_tenantId_idx: index(
        'service_line_pickup_order_id_tenantId_idx',
      ).on(table.pickupOrderId, table.tenantId),
      tenantId_idx: index('service_line_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('service_line_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('service_line_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('service_line_versionNumber_idx').on(
        table.versionNumber,
      ),
      assetId_idx: index('service_line_assetId_idx').on(table.assetId),
      mobile_on_site_asset_idx: index('service_line_mobile_on_site_asset_idx')
        .on(table.tenantId, table.customerSiteId, table.productId, table.assetId)
        .where(sql`${table.archivedAtTs} IS NULL AND ${table.sl_status} = 'DELIVERED'`),
      dumpOrganizationSiteIdSetByExtractionRunId_idx: index(
        'service_line_dumpOrganizationSiteIdSetByExtractionRunId_idx',
      ).on(table.dumpOrganizationSiteIdSetByExtractionRunId),
      dumpOrganizationSiteId_idx: index(
        'service_line_dumpOrganizationSiteId_idx',
      ).on(table.dumpOrganizationSiteId),
      materialId_idx: index('service_line_materialId_idx').on(table.materialId),
      productId_idx: index('service_line_productId_idx').on(table.productId),
      skuId_idx: index('service_line_skuId_idx').on(table.skuId),
      subHaulerId_idx: index('service_line_subHaulerId_idx').on(
        table.subHaulerId,
      ),
      businessLineId_idx: index('service_line_businessLineId_idx').on(
        table.businessLineId,
      ),
      lastAssetMovementId_idx: index('service_line_lastAssetMovementId_idx').on(
        table.lastAssetMovementId,
      ),
      rentalChainId_idx: index('service_line_rentalChainId_idx').on(
        table.rentalChainId,
      ),
      serviceLineV2Id_idx: index('service_line_serviceLineV2Id_idx').on(
        table.serviceLineV2Id,
      ),
      // Composite index for open rentals query - filters on tenantId, sl_status, archivedAtTs
      open_rentals_idx: index('service_line_open_rentals_idx').on(
        table.tenantId,
        table.sl_status,
        table.archivedAtTs,
      ),
      sms_context_customer_active_idx: index(
        'service_line_sms_context_customer_active_idx',
      )
        .on(
          table.tenantId,
          table.customerId,
          sql`(CASE "sl_status" WHEN 'DELIVERED'::public.service_line_status THEN 0 WHEN 'SCHEDULED'::public.service_line_status THEN 1 ELSE 2 END)`,
          sql`"lastDeliveredDate" DESC NULLS LAST`,
          sql`"sl_deliveryDate" DESC NULLS LAST`,
          table.createdAtTs.desc(),
          table.id.desc(),
        )
        .where(
          sql`"archivedAtTs" IS NULL AND "customerId" IS NOT NULL AND "sl_status" IN ('SCHEDULED', 'DELIVERED')`,
        ),
      sms_context_site_active_idx: index(
        'service_line_sms_context_site_active_idx',
      )
        .on(
          table.tenantId,
          table.customerSiteId,
          sql`(CASE "sl_status" WHEN 'DELIVERED'::public.service_line_status THEN 0 WHEN 'SCHEDULED'::public.service_line_status THEN 1 ELSE 2 END)`,
          sql`"lastDeliveredDate" DESC NULLS LAST`,
          sql`"sl_deliveryDate" DESC NULLS LAST`,
          table.createdAtTs.desc(),
          table.id.desc(),
        )
        .where(
          sql`"archivedAtTs" IS NULL AND "customerSiteId" IS NOT NULL AND "sl_status" IN ('SCHEDULED', 'DELIVERED')`,
        ),
      closed_rentals_site_latest_idx: index(
        'service_line_closed_rentals_site_latest_idx',
      )
        .on(
          table.tenantId,
          table.customerSiteId,
          sql`"sl_pickupDate" DESC NULLS LAST`,
          sql`"sl_deliveryDate" DESC NULLS LAST`,
          table.createdAtTs.desc(),
          table.id.desc(),
        )
        .where(sql`"archivedAtTs" IS NULL AND "sl_status" = 'REMOVED'`),
      rentalNoteCommentId_idx: index('service_line_rentalNoteCommentId_idx').on(
        table.rentalNoteCommentId,
      ),
      // Partial index for rental note lookups - only index rows with rental notes
      rental_note_lookup_idx: index('service_line_rental_note_lookup_idx')
        .on(table.tenantId, table.rentalNoteCommentId)
        .where(sql`"rentalNoteCommentId" IS NOT NULL`),

      //foreign keys
      service_line_assetId_fkey: foreignKey({
        columns: [table.assetId],
        foreignColumns: [assets.id],
        name: 'service_line_assetId_fkey',
      }),
      service_line_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'service_line_customerId_fkey',
      }),
      service_line_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'service_line_customerSiteId_fkey',
      }),
      service_line_dumpOrganizationSiteId_fkey: foreignKey({
        columns: [table.dumpOrganizationSiteId],
        foreignColumns: [organization_site.id],
        name: 'service_line_dumpOrganizationSiteId_fkey',
      }),
      service_line_dumpOrganizationSiteIdSetByExtractionRunId_fkey: foreignKey({
        columns: [table.dumpOrganizationSiteIdSetByExtractionRunId],
        foreignColumns: [dump_ticket_extraction_run.id],
        name: 'service_line_dumpOrganizationSiteIdSetByExtractionRunId_fkey',
      }),
      service_line_materialId_fkey: foreignKey({
        columns: [table.materialId],
        foreignColumns: [material.id],
        name: 'service_line_materialId_fkey',
      }),
      service_line_productId_fkey: foreignKey({
        columns: [table.productId],
        foreignColumns: [product.id],
        name: 'service_line_productId_fkey',
      }),
      service_line_skuId_fkey: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'service_line_skuId_fkey',
      }),
      service_line_subHaulerId_fkey: foreignKey({
        columns: [table.subHaulerId],
        foreignColumns: [sub_hauler.id],
        name: 'service_line_subHaulerId_fkey',
      }),
    };
  },
);

/**
 * Historical record of changes to service lines.
 * Tracks all modifications to service line records over time for auditing and versioning.
 * Contains the same fields as service_line but allows nulls for partial updates.
 */
export const service_line_history = pgTable(
  'service_line_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    reasonForChange: text('reasonForChange'),
    customerSiteId: uuid('customerSiteId'),
    deliveryOrderId: uuid('deliveryOrderId'),
    materialId: uuid('materialId'),
    pickupOrderId: uuid('pickupOrderId'),
    productId: uuid('productId'),
    activeAtTs: timestamp('activeAtTs', { withTimezone: true, mode: 'string' }),
    dumpOrganizationSiteId: uuid('dumpOrganizationSiteId'),
    endedAtTs: timestamp('endedAtTs', { withTimezone: true, mode: 'string' }),
    assetId: uuid('assetId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    skuId: uuid('skuId'),
    dumpDate: date('dumpDate'),
    subHaulerId: uuid('subHaulerId'),
    totalDumpCost: bigint('totalDumpCost', { mode: 'number' }),
    dumpCostSetManually: boolean('dumpCostSetManually'),
    dumpOrganizationSiteIdSetByExtractionRunId: uuid(
      'dumpOrganizationSiteIdSetByExtractionRunId',
    ),
    lastAutomatedChargeDate: date('lastAutomatedChargeDate'),
    lastAutomatedMultipleChargeDate: date('lastAutomatedMultipleChargeDate'),
    lastAutomatedChargeOfDaysDate: date('lastAutomatedChargeOfDaysDate'),
    pickupDateBillingOverride: date('pickupDateBillingOverride'),
    lastServiceDate: date('lastServiceDate'),
    notes: text('notes'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    customerId: uuid('customerId'),
    lastDeliveredDate: date('lastDeliveredDate'),
    rentalNumber: varchar('rentalNumber', { length: 255 }),
    serialNumber: integer('serialNumber'),
    lastAssetMovementId: uuid('lastAssetMovementId'),
    quantity: integer('quantity'),
    rentalChainId: uuid('rentalChainId'),
    serviceLineV2Id: uuid('serviceLineV2Id'),
    businessLineId: uuid('businessLineId'),
    sl_deliveryDate: date('sl_deliveryDate', { mode: 'date' }),
    sl_pickupDate: date('sl_pickupDate', { mode: 'date' }),
    sl_status: service_line_status('sl_status'),
    sl_daysOnSite: integer('sl_daysOnSite'),
    sl_billablePickupDate: date('sl_billablePickupDate', { mode: 'date' }),
    sl_deliveryIsCompleted: boolean('sl_deliveryIsCompleted'),
    sl_pickupIsCompleted: boolean('sl_pickupIsCompleted'),
    sl_validDeliveryOrderId: uuid('sl_validDeliveryOrderId'),
    geofenceRadiusMeters: smallint('geofenceRadiusMeters'),
    rentalNoteCommentId: uuid('rentalNoteCommentId'),
  },
  (table) => {
    return {
      site_merge_page_idx: index('service_line_history_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.versionId,
      ),
      idx_service_line_history_id_versionnumber: index(
        'idx_service_line_history_id_versionnumber',
      ).on(table.id, table.versionNumber),
      customerSiteId_idx: index('service_line_history_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      deliveryOrderId_idx: index('service_line_history_deliveryOrderId_idx').on(
        table.deliveryOrderId,
      ),
      pickupOrderId_idx: index('service_line_history_pickupOrderId_idx').on(
        table.pickupOrderId,
      ),
    };
  },
);

/** Type inference for the service line schema */
export type ServiceLineSchema = typeof service_line.$inferSelect;
